import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PromoForm } from "@/components/PromoForm";
import { AdminNav } from "@/components/AdminNav";
import { fmtDate } from "@/components/PriceBlock";

const status = (p) => {
  if (!p.active) return ["Inactive", "text-slate-400 border-line"];
  if (p.expires_at && p.expires_at < new Date().toISOString()) return ["Expired", "text-rose-300 border-rose-500/30"];
  if (p.max_uses && p.times_used >= p.max_uses) return ["Used up", "text-rose-300 border-rose-500/30"];
  return ["Active", "text-emerald-300 border-emerald-500/30"];
};
const discount = (p) => (p.kind === "percent" ? `${p.value}% off` : `$${p.value.toFixed(2)} off`);
const plans = (p) => p.plans.map((x) => (x === "year" ? "Yearly" : "Monthly")).join(" + ");

const PromoRow = ({ p, onToggle }) => {
  const [label, cls] = status(p);
  return (
    <tr className="border-t border-line" data-testid={`promo-row-${p.code}`}>
      <td className="py-3 pr-4 font-code tracking-widest text-amber-100">{p.code}</td>
      <td className="py-3 pr-4 text-slate-300">{discount(p)}</td>
      <td className="py-3 pr-4 text-slate-400">{plans(p)}</td>
      <td className="py-3 pr-4 font-code text-slate-300" data-testid={`promo-used-${p.code}`}>{p.times_used}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
      <td className="py-3 pr-4 text-slate-400">{p.expires_at ? fmtDate(p.expires_at) : "Never"}</td>
      <td className="py-3 pr-4"><span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-code ${cls}`} data-testid={`promo-status-${p.code}`}>{label}</span></td>
      <td className="py-3 text-right">
        <button onClick={() => onToggle(p)} className="text-xs text-slate-400 hover:text-amber-300" data-testid={`promo-toggle-${p.code}`}>{p.active ? "Deactivate" : "Activate"}</button>
      </td>
    </tr>
  );
};

export default function AdminPromos() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const isAdmin = user?.role === "admin";
  useEffect(() => { if (isAdmin) api.get("/admin/promo-codes").then((r) => setCodes(r.data)); }, [isAdmin]);
  if (!isAdmin) return <Navigate to="/worlds" replace />;

  const toggle = async (p) => {
    try {
      const { data } = await api.post(`/admin/promo-codes/${p.id}/${p.active ? "deactivate" : "activate"}`);
      setCodes((list) => list.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="relative z-10 min-h-screen px-6 md:px-12 py-10 max-w-6xl mx-auto" data-testid="admin-promos-page">
      <Link to="/worlds" className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-10 w-fit" data-testid="admin-back-link"><ArrowLeft className="w-4 h-4" /> Back to worlds</Link>
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Admin</p>
      <h1 className="font-display text-4xl sm:text-5xl text-amber-50 mb-3">Promo codes</h1>
      <p className="text-sm text-slate-400 mb-8">Discounts apply to the first payment only. Each account can use a code once.</p>
      <AdminNav />
      <PromoForm onCreated={(p) => setCodes((list) => [p, ...list])} />
      <div className="mt-10 overflow-x-auto rounded-2xl border border-line bg-panel px-6 py-2">
        <table className="w-full text-sm min-w-[720px]" data-testid="promo-table">
          <thead><tr className="text-left font-code text-[11px] uppercase tracking-widest text-slate-500">
            <th className="py-3 pr-4 font-normal">Code</th><th className="py-3 pr-4 font-normal">Discount</th><th className="py-3 pr-4 font-normal">Plans</th>
            <th className="py-3 pr-4 font-normal">Used</th><th className="py-3 pr-4 font-normal">Expires</th><th className="py-3 pr-4 font-normal">Status</th><th />
          </tr></thead>
          <tbody>
            {codes.length === 0 && <tr><td colSpan={7} className="py-6 text-slate-500" data-testid="promo-empty">No codes yet.</td></tr>}
            {codes.map((p) => <PromoRow key={p.id} p={p} onToggle={toggle} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
